import { Flex, PremiumLineItem, smBumps, Typo } from '@wrisk/ui-components'
import React, { useMemo } from 'react'

import { Promotion } from '../../../../domain'
import { QuotePremiums } from '../../../../hooks/premiums'
import {
  tFormats,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { toDateTime } from '../../../../util/date'
import { usePaymentSchedule } from '../../../product/paymentScheduleContext'
import {
  additionalLineItems,
  coreLineItems,
  discountLineItems,
  optionalLineItems,
} from '../lineItems'

export const useStandardAdjustedPremium = ({
  currentPremiums,
  previousPremiums,
  promotion,
  tKey,
}: {
  currentPremiums: QuotePremiums
  previousPremiums?: QuotePremiums
  promotion?: Promotion
  tKey: TKeyBuilder
}) => {
  const { t } = useWriskTranslation()
  const { paymentScheduleType } = usePaymentSchedule()

  const currentAmount = t(tFormats('currency.long'), {
    amount: currentPremiums.totals.discounted,
  })

  const previousAmount =
    previousPremiums &&
    t(tFormats('currency.long'), {
      amount: previousPremiums.totals.discounted,
    })

  return useMemo(() => {
    const header = t(tKey(paymentScheduleType, 'header'))

    const promo = promotion?.endAt
      ? t(tKey('promotion'), {
          date: t(tFormats('date.long'), { value: toDateTime(promotion.endAt) }),
        })
      : undefined

    const lineItems: PremiumLineItem[] = [
      ...coreLineItems(t, tKey, currentPremiums, previousPremiums),
      ...discountLineItems(t, tKey, currentPremiums, previousPremiums),
      ...optionalLineItems(t, currentPremiums, previousPremiums),
    ]

    const additionalItems = additionalLineItems(t, tKey, currentPremiums)

    const yearlyTotalFormatted = t(tFormats('currency.long'), {
      amount: currentPremiums.totals.yearly,
    })

    const yearlyTotalLabel = t(tKey('yearly-total'), { defaultValue: null })

    const yearlyTotal = t(tKey('yearly-total-amount'), {
      amount: yearlyTotalFormatted,
      defaultValue: null,
    })

    const showYearlyTotalSection =
      paymentScheduleType === 'MONTHLY' && yearlyTotalLabel && yearlyTotalFormatted

    const extraInfo = (
      <Flex
        flexDirection='column'
        width={1}
        borderTopWidth={1}
        py={smBumps}
        gap={smBumps}
      >
        <Flex width={1}>
          <Typo typoSize='sm' fontWeight='bold'>
            {header}
          </Typo>
          <Typo typoSize='sm' fontWeight='bold'>
            {currentAmount}
          </Typo>
        </Flex>
        {showYearlyTotalSection && (
          <Flex width={1}>
            <Typo typoSize='sm'>{yearlyTotalLabel}</Typo>
            <Typo typoSize='sm'>{yearlyTotalFormatted}</Typo>
          </Flex>
        )}
      </Flex>
    )

    return {
      header,
      extraInfo,
      lineItems,
      additionalItems,
      currentAmount,
      previousAmount,
      yearlyTotal,
      promo,
    }
  }, [
    t,
    tKey,
    currentPremiums,
    previousPremiums,
    promotion,
    paymentScheduleType,
    currentAmount,
    previousAmount,
  ])
}
